/* @flow */
import HttpAdapter from './HttpAdapter';
import StringUtils from 'common/utils/StringUtils';
import DataUtils from 'common/utils/DataUtils';
import _ from 'lodash';

import UserAnalyticsService from 'common/services/UserAnalyticsService';

type Interceptor = (request: Request, response: Response) => Promise<$FixMe>;
type Interceptors = {
    [statusCode: number | string]: Interceptor
};

const logTiming = (target, key, descriptor) => {
    const fn = descriptor.value;
    return {
        configurable: true,
        get() {
            const loggedFn = (...args) => {
                const before = performance.now();

                const analyticsInterceptor = (request, response) => {
                    return new Promise(resolve => {
                        const [params] = args;
                        const cleanedParams = {};
                        Object.keys(params).forEach(key => {
                            if (params[key]) {
                                cleanedParams[key] = `{${key}}`;
                            }
                        });
                        const sanitizedEndpoint = santizeEndpoint(
                            this.getEndPoint(),
                            cleanedParams,
                            this.getIdAttrs(),
                            this.getUrlKeys(this.getEndPoint())
                        );
                        const after = performance.now();
                        UserAnalyticsService.setApiTiming(
                            request.options.method,
                            sanitizedEndpoint,
                            after - before
                        );
                        resolve(response);
                    });
                };
                this.setInterceptor(analyticsInterceptor);
                const promise = fn.apply(this, args);

                return promise;
            };
            Object.defineProperty(this, key, {
                value: loggedFn,
                configurable: true,
                writable: true
            });
            return loggedFn;
        }
    };
};

export type Config = {
    resourceName?: string,
    idAttrs?: string,
    endPoint: string,
    baseUrl: string,
    defaultParams?: { [key: string]: string }
};

class ApiModel {
    static defaultHeaders: { [name: string]: string } = {};
    static defaultInterceptors: Interceptors = {};

    _model: {
        resourceName: ?string,
        idAttrs: ?string,
        baseUrl: ?string,
        endPoint: string,
        interceptors: Interceptors,
        defaultParams: ?{ [key: string]: string }
    };

    constructor(config: Config) {
        this._model = {};
        this._model.resourceName = config.resourceName;
        this._model.idAttrs = config.idAttrs;
        this._model.baseUrl = config.baseUrl;
        this._model.endPoint = config.endPoint;
        this._model.defaultParams = config.defaultParams;
        this._model.interceptors = {};
    }

    getIdAttrs = function () {
        return this._model.idAttrs;
    };

    getEndPoint = function () {
        return this._model.endPoint;
    };

    getDefaultAdapter = function () {
        return HttpAdapter(this.getDefaultHeaders(), this.getInterceptors());
    };

    @logTiming
    fetch(params, abortSignal?: AbortSignal) {
        return this.getDefaultAdapter().get(this.getUrl(params), abortSignal);
    }
    @logTiming
    delete(params, abortSignal?: AbortSignal) {
        return this.getDefaultAdapter().delete(
            this.getUrl(params),
            abortSignal
        );
    }

    @logTiming
    head(params, abortSignal?: AbortSignal) {
        return this.getDefaultAdapter().head(this.getUrl(params), abortSignal);
    }

    @logTiming
    post(params, body, headers, abortSignal?: AbortSignal) {
        return this.getDefaultAdapter().post(
            this.getUrl(params),
            body,
            headers,
            abortSignal
        );
    }

    @logTiming
    put(params, body, headers, abortSignal?: AbortSignal) {
        return this.getDefaultAdapter().put(
            this.getUrl(params),
            body,
            headers,
            abortSignal
        );
    }

    @logTiming
    patch(params, body, headers, abortSignal?: AbortSignal) {
        return this.getDefaultAdapter().patch(
            this.getUrl(params),
            body,
            headers,
            abortSignal
        );
    }

    getFullUrl = function () {
        return `${this._model.baseUrl}${this._model.endPoint}`;
    };

    getUrl = function (params) {
        const fullUrl = this.getFullUrl();
        return santizeUrl(
            fullUrl,
            { ...this._model.defaultParams, ...params },
            this._model.idAttrs,
            this.getUrlKeys(fullUrl)
        );
    };

    getUrlKeys = function (url) {
        var re = new RegExp('{[A-Za-z0-9_]*}', 'g');
        var keys = url.match(re);
        return keys;
    };

    getDefaultHeaders = function () {
        return ApiModel.defaultHeaders;
    };

    setInterceptors(interceptors: Interceptors) {
        this._model.interceptors = interceptors;
    }

    getInterceptors(): Interceptors {
        return {
            ...ApiModel.defaultInterceptors,
            ...this._model.interceptors
        };
    }

    getResourceName = function () {
        return this._model.resourceName;
    };

    get endpoint() {
        return this._model.endPoint;
    }

    get baseUrl() {
        return this._model.baseUrl;
    }

    setInterceptor(interceptor: Interceptor, statusCode?: ?number) {
        if (!statusCode) {
            this._model.interceptors.all = interceptor;
            return;
        }
        this._model.interceptors[statusCode] = interceptor;
    }

    static setDefaultInterceptor(promise: Interceptor, statusCode: number) {
        ApiModel.defaultInterceptors[statusCode] = promise;
    }
}

const santizeEndpoint = function (url, params = {}, id, urlKeys) {
    var keyWithoutParenthesis;
    var value;
    var urlWithoutQueryParams = urlKeys
        ? urlKeys.reduce((previousValue, key) => {
              keyWithoutParenthesis = StringUtils.trimFirstAndLastCharacter(
                  key
              );
              value = params[keyWithoutParenthesis];
              if (!DataUtils.isEmpty(value)) {
                  return previousValue.replace(key, value);
                  // if value exists in 'params', replce the {key} with actual value
              } else {
                  if (keyWithoutParenthesis === id) {
                      return previousValue.replace(`/${key}`, '');
                      // if key is one of idAttrs, & doesn't exist in params, remove
                      // ideally, either idAttrs, or params will have all keys
                  }
                  throw new Error(`Missing Params: ${keyWithoutParenthesis}`);
              }
          }, url)
        : url;
    return urlWithoutQueryParams;
};

const santizeUrl = function (url, params = {}, id, urlKeys) {
    var queryParams = getQueryParamsFromParams(params, urlKeys);
    return constructUrlFromQueryParams(
        santizeEndpoint(url, params, id, urlKeys),
        queryParams
    );
};

const getQueryParamsFromParams = function (params, urlKeys) {
    return urlKeys
        ? urlKeys.reduce((previousValue, urlKey) => {
              // omit urlKeys if present in params
              return _.omit(
                  previousValue,
                  StringUtils.trimFirstAndLastCharacter(urlKey)
              );
          }, params)
        : {};
};

const constructUrlFromQueryParams = function (url, queryParams) {
    return Object.keys(queryParams).reduce((previousUrl, key, index) => {
        const encodedQueryParam = encodeURIComponent(queryParams[key]);
        if (index === 0) {
            return `${previousUrl}?${key}=${encodedQueryParam}`;
        } else {
            return `${previousUrl}&${key}=${encodedQueryParam}`;
        }
    }, url);
};

export default ApiModel;
export { ApiModel };
